/**
 * Advanced Job Matching Algorithm
 * Predicts candidate's chances of getting selected based on resume-job fit
 * Uses multiple factors: skills, experience, education, keywords
 */

export interface MatchAnalysis {
  overallScore: number; // 0-100
  fitLevel: 'excellent' | 'good' | 'moderate' | 'low' | 'poor';
  recommendation: string;
  shouldOptimize: boolean;
  factors: {
    skillMatch: number;
    experienceMatch: number;
    keywordMatch: number;
    educationMatch: number;
  };
  missingCriticalSkills: string[];
  matchedSkills: string[];
  insights: string[];
  warnings: string[];
}

interface ResumeProfile {
  skills: string[];
  experience: string;
  education: string;
  keywords: string[];
}

interface JobProfile {
  requiredSkills: string[];
  preferredSkills: string[];
  requirements: string[];
  description: string;
  experienceLevel?: string;
}

/**
 * Normalize text for better matching
 */
function normalizeText(text: string): string {
  return text.toLowerCase().trim().replace(/[^a-z0-9\s+#]/g, '');
}


/**
 * Calculate skill match score
 */
function calculateSkillMatch(
  resumeSkills: string[],
  requiredSkills: string[],
  preferredSkills: string[]
): {
  score: number;
  matched: string[];
  missing: string[];
} {
  const normalizedResumeSkills = new Set(resumeSkills.map(normalizeText));
  const normalizedRequiredSkills = requiredSkills.map(normalizeText);
  const normalizedPreferredSkills = preferredSkills.map(normalizeText);
  
  // Find matched and missing required skills
  const matchedRequired = normalizedRequiredSkills.filter(skill => 
    Array.from(normalizedResumeSkills).some(rs => 
      rs.includes(skill) || skill.includes(rs)
    )
  );
  
  const missingRequired = normalizedRequiredSkills.filter(skill => 
    !Array.from(normalizedResumeSkills).some(rs => 
      rs.includes(skill) || skill.includes(rs)
    )
  );
  
  // Find matched preferred skills
  const matchedPreferred = normalizedPreferredSkills.filter(skill => 
    Array.from(normalizedResumeSkills).some(rs => 
      rs.includes(skill) || skill.includes(rs)
    )
  );
  
  // Calculate score (required skills weighted 70%, preferred 30%)
  const requiredScore = normalizedRequiredSkills.length > 0 
    ? (matchedRequired.length / normalizedRequiredSkills.length) * 70 
    : 35; // If no required skills specified, give partial credit
  
  const preferredScore = normalizedPreferredSkills.length > 0 
    ? (matchedPreferred.length / normalizedPreferredSkills.length) * 30 
    : 30; // If no preferred skills specified, give full credit
  
  return {
    score: Math.round(requiredScore + preferredScore),
    matched: [...matchedRequired, ...matchedPreferred],
    missing: missingRequired
  };
}

/**
 * Extract years of experience from text
 */
function extractYearsOfExperience(text: string): number {
  const patterns = [
    /(\d+)\+?\s*years?\s+(?:of\s+)?experience/i,
    /experience\s*:?\s*(\d+)\+?\s*years?/i,
    /(\d+)\+?\s*yrs?\s+(?:of\s+)?experience/i
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return parseInt(match[1]);
    }
  }
  
  // Estimate from job titles and dates
  const yearRanges = text.match(/\d{4}\s*-\s*(?:\d{4}|present|current)/gi);
  if (yearRanges) {
    let totalYears = 0;
    yearRanges.forEach(range => {
      const [start, end] = range.split('-').map(y => {
        const year = y.trim().toLowerCase();
        if (year === 'present' || year === 'current') return new Date().getFullYear();
        return parseInt(year);
      });
      totalYears += end - start;
    });
    return totalYears;
  }
  
  return 0;
}

/**
 * Calculate experience match
 */
function calculateExperienceMatch(
  resumeExperience: string,
  jobRequirements: string[]
): number {
  const resumeYears = extractYearsOfExperience(resumeExperience);
  
  // Extract required years from job requirements
  const requirementText = jobRequirements.join(' ');
  const requiredYears = extractYearsOfExperience(requirementText);
  
  if (requiredYears === 0) return 85; // No specific requirement, give high score
  
  const ratio = resumeYears / requiredYears;
  
  if (ratio >= 1.5) return 100; // Over-qualified
  if (ratio >= 1.0) return 95;  // Perfect match
  if (ratio >= 0.8) return 80;  // Close match
  if (ratio >= 0.6) return 60;  // Moderate match
  if (ratio >= 0.4) return 40;  // Low match
  return 20; // Poor match
}

/**
 * Calculate keyword match using TF-IDF inspired approach
 */
function calculateKeywordMatch(
  resumeText: string,
  jobDescription: string,
  jobRequirements: string[]
): number {
  const jobText = `${jobDescription} ${jobRequirements.join(' ')}`;
  
  // Extract important keywords from job (excluding common words)
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'should', 'could', 'may', 'might', 'must', 'can']);
  
  const jobWords = normalizeText(jobText)
    .split(/\s+/)
    .filter(word => word.length > 3 && !stopWords.has(word));
  
  const resumeWords = new Set(normalizeText(resumeText).split(/\s+/));
  
  // Calculate match percentage
  const matchedWords = jobWords.filter(word => resumeWords.has(word));
  const uniqueJobWords = [...new Set(jobWords)];
  
  return uniqueJobWords.length > 0 
    ? Math.round((matchedWords.length / uniqueJobWords.length) * 100) 
    : 50;
}

/**
 * Calculate education match
 */
function calculateEducationMatch(
  resumeEducation: string,
  jobRequirements: string[]
): number {
  const resumeNormalized = normalizeText(resumeEducation);
  const requirementText = normalizeText(jobRequirements.join(' '));
  
  // Check for degree levels
  const hasPHD = resumeNormalized.includes('phd') || resumeNormalized.includes('doctorate');
  const hasMasters = resumeNormalized.includes('master') || resumeNormalized.includes('msc') || resumeNormalized.includes('mba');
  const hasBachelors = resumeNormalized.includes('bachelor') || resumeNormalized.includes('bsc') || resumeNormalized.includes('btech') || resumeNormalized.includes('be ');
  
  const requiresPHD = requirementText.includes('phd') || requirementText.includes('doctorate');
  const requiresMasters = requirementText.includes('master') || requirementText.includes('msc') || requirementText.includes('mba');
  const requiresBachelors = requirementText.includes('bachelor') || requirementText.includes('degree');
  
  // If no specific education requirement
  if (!requiresPHD && !requiresMasters && !requiresBachelors) {
    return hasBachelors || hasMasters || hasPHD ? 100 : 70;
  }
  
  // Match education level
  if (requiresPHD && hasPHD) return 100;
  if (requiresPHD && hasMasters) return 70;
  if (requiresMasters && (hasMasters || hasPHD)) return 100;
  if (requiresMasters && hasBachelors) return 80;
  if (requiresBachelors && (hasBachelors || hasMasters || hasPHD)) return 100;
  
  return 50; // Partial credit
}

/**
 * Main matching algorithm
 */
export function analyzeJobMatch(
  resumeProfile: ResumeProfile,
  jobProfile: JobProfile
): MatchAnalysis {
  console.log('[JobMatching] Analyzing job fit...', { resumeProfile, jobProfile });
  
  // Calculate individual factor scores
  const skillAnalysis = calculateSkillMatch(
    resumeProfile.skills,
    jobProfile.requiredSkills,
    jobProfile.preferredSkills
  );
  
  const experienceScore = calculateExperienceMatch(
    resumeProfile.experience,
    jobProfile.requirements
  );
  
  const keywordScore = calculateKeywordMatch(
    `${resumeProfile.skills.join(' ')} ${resumeProfile.experience} ${resumeProfile.education}`,
    jobProfile.description,
    jobProfile.requirements
  );
  
  const educationScore = calculateEducationMatch(
    resumeProfile.education,
    jobProfile.requirements
  );
  
  // Weighted overall score
  // Skills: 40%, Experience: 30%, Keywords: 20%, Education: 10%
  const overallScore = Math.round(
    skillAnalysis.score * 0.40 +
    experienceScore * 0.30 +
    keywordScore * 0.20 +
    educationScore * 0.10
  );
  
  // Determine fit level - ALWAYS allow optimization (business decision)
  let fitLevel: MatchAnalysis['fitLevel'];
  let shouldOptimize: boolean;
  let recommendation: string;
  
  if (overallScore >= 80) {
    fitLevel = 'excellent';
    shouldOptimize = true;
    recommendation = 'Excellent match! Your profile strongly aligns with this role. Proceed with optimization for best results.';
  } else if (overallScore >= 65) {
    fitLevel = 'good';
    shouldOptimize = true;
    recommendation = 'Good match! With optimization, your chances of getting selected are high.';
  } else if (overallScore >= 50) {
    fitLevel = 'moderate';
    shouldOptimize = true;
    recommendation = 'Moderate fit. Optimization can help improve your resume for this role, though consider if it aligns with your career goals.';
  } else if (overallScore >= 35) {
    fitLevel = 'low';
    shouldOptimize = true; // Changed to true - allow optimization
    recommendation = 'Lower fit detected. Optimization will help, but be aware this role may be challenging to secure. Consider upskilling in missing areas.';
  } else {
    fitLevel = 'poor';
    shouldOptimize = true; // Changed to true - allow optimization
    recommendation = 'Limited match. While optimization will improve your resume, success rate may be lower. We suggest also exploring better-fit positions.';
  }
  
  // Generate insights
  const insights: string[] = [];
  const warnings: string[] = [];
  
  if (skillAnalysis.score >= 70) {
    insights.push(`Strong skill alignment (${skillAnalysis.matched.length} matching skills)`);
  } else if (skillAnalysis.score < 50) {
    warnings.push(`Only ${skillAnalysis.matched.length} matching skills found. Consider upskilling in missing areas.`);
  }
  
  if (experienceScore >= 80) {
    insights.push('Experience level matches or exceeds requirements');
  } else if (experienceScore < 60) {
    warnings.push('Experience level may be below requirements');
  }
  
  if (skillAnalysis.missing.length > 0) {
    warnings.push(`Missing ${skillAnalysis.missing.length} critical skills: ${skillAnalysis.missing.slice(0, 3).join(', ')}${skillAnalysis.missing.length > 3 ? '...' : ''}`);
  }
  
  if (keywordScore < 40) {
    warnings.push('Low keyword match. Resume may not pass ATS screening.');
  }
  
  console.log('[JobMatching] Analysis complete', {
    overallScore,
    fitLevel,
    factors: {
      skillMatch: skillAnalysis.score,
      experienceMatch: experienceScore,
      keywordMatch: keywordScore,
      educationMatch: educationScore
    }
  });
  
  return {
    overallScore,
    fitLevel,
    recommendation,
    shouldOptimize,
    factors: {
      skillMatch: skillAnalysis.score,
      experienceMatch: experienceScore,
      keywordMatch: keywordScore,
      educationMatch: educationScore
    },
    missingCriticalSkills: skillAnalysis.missing,
    matchedSkills: skillAnalysis.matched,
    insights,
    warnings
  };
}

/**
 * Quick helper to provide optimization guidance (no longer blocks)
 */
export function getOptimizationGuidance(matchAnalysis: MatchAnalysis): {
  proceed: boolean;
  showWarning: boolean;
  warningMessage: string;
} {
  // Business decision: ALWAYS allow optimization, but show warnings for low fits
  const proceed = true;
  
  if (matchAnalysis.overallScore < 35) {
    return {
      proceed,
      showWarning: true,
      warningMessage: 'Limited job fit. While we can optimize your resume, consider that success rate may be lower for roles with significant skill gaps. Exploring better-fit positions may yield better results.'
    };
  }
  
  if (matchAnalysis.missingCriticalSkills.length >= 5) {
    return {
      proceed,
      showWarning: true,
      warningMessage: `${matchAnalysis.missingCriticalSkills.length} critical skills are missing. Optimization will help present your existing skills better, but consider upskilling in: ${matchAnalysis.missingCriticalSkills.slice(0, 3).join(', ')}.`
    };
  }
  
  if (matchAnalysis.overallScore < 50) {
    return {
      proceed,
      showWarning: true,
      warningMessage: 'Moderate skill gap detected. We\'ll optimize your resume to highlight transferable skills and relevant experience.'
    };
  }
  
  return {
    proceed,
    showWarning: false,
    warningMessage: ''
  };
}

